import { useState } from 'react';
import { useI18n } from '@/hooks/useI18n';
import { Building2, User, CreditCard, AlertCircle, Info, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { IPaymentPlan } from '@/lib/builder/models/payment-plan.model';
import {
  hasMultipleInstallments,
  hasPaymentInFlight,
} from '@/lib/builder/utils/payment-plan.utils';
import { isItinerarySettled } from '@/lib/builder/utils/itinerary-lock';
import { InstallmentStatusBadge } from '@/lib/builder/components/payment-plan/InstallmentStatusBadge';
import { sharedPaymentErrorKey } from '@/lib/shared-proposal/utils/payment-error';
import { ILegalSettings, hasLegalDocuments } from '@/lib/legal/legal.model';
import { LegalAcceptance } from '@/lib/legal/LegalAcceptance';

interface SharedProposalHeaderProps {
  sharedBy: {
    first_name: string;
    last_name: string;
    logo_url?: string;
    logo_small_url?: string;
    agency_name?: string;
  };
  totalPrice?: number | null;
  cancellationPolicyNotes?: string | null;
  /** L'advisor ha Stripe collegato: senza, l'header resta puramente informativo. */
  paymentEnabled?: boolean;
  /**
   * Piano di pagamento dell'itinerario, se ne ha uno.
   *
   * L'header non lo mostra: gli serve per sapere se la CTA sul totale ha ancora senso.
   * Prima non lo riceveva, e il pulsante "Paga ora" compariva anche su un itinerario gia'
   * saldato o con un bonifico in volo — cioe' proprio nei due casi in cui costa un secondo
   * addebito vero.
   */
  plan?: IPaymentPlan | null;
  onPayNow?: () => void;
  /**
   * true mentre si sta creando la Checkout Session, cioe' fra il click e il redirect su
   * Stripe. NON e' lo stato del pagamento: quello si legge dal piano. Si chiamava
   * `isProcessing` e si confondeva con "pagamento in corso", che ora esiste davvero.
   */
  isCreatingLink?: boolean;
  showError?: boolean;
  errorMessage?: string;
  /** Testi legali di chi incassa: se ce n'e' almeno uno, il pagamento passa dalla spunta. */
  legal?: ILegalSettings | null;
  legalAccepted?: boolean;
  onLegalAcceptedChange?: (accepted: boolean) => void;
  /** false finche' i termini non sono accettati. Deciso a monte: la spunta e' unica per pagina. */
  canPay?: boolean;
}

const formatPrice = (price: number | null | undefined) => {
  if (!price) return '€0.00';
  return new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: 'EUR'
  }).format(price);
};

export const SharedProposalHeader = ({
  sharedBy,
  totalPrice,
  cancellationPolicyNotes,
  paymentEnabled = false,
  plan = null,
  onPayNow,
  isCreatingLink = false,
  showError = false,
  errorMessage,
  legal = null,
  legalAccepted = false,
  onLegalAcceptedChange,
  canPay = true,
}: SharedProposalHeaderProps) => {
  const { t } = useI18n();
  const [showPolicyModal, setShowPolicyModal] = useState(false);

  // Con piu' rate si paga dal pannello, una alla volta: qui la CTA sul totale chiederebbe
  // l'intera cifra anche a chi ha gia' versato l'acconto.
  const payPerInstallment = hasMultipleInstallments(plan);
  const settled = isItinerarySettled(plan);
  const paymentInFlight = hasPaymentInFlight(plan);

  // Un pagamento in volo NON toglie il pulsante: la riga 'pending' nasce dal semplice
  // arrivo del cliente sul bonifico in Checkout, quindi chi ha aperto il checkout e non ha
  // concluso resterebbe senza alcun modo di ripartire. Il tentativo aperto si dice con un
  // avviso accanto alla CTA, non nascondendola.
  const canPayTotal = paymentEnabled && !payPerInstallment && !settled;

  console.log('[SharedProposalHeader] Props:', {
    paymentEnabled,
    totalPrice,
    hasSharedBy: !!sharedBy,
    payPerInstallment,
    settled,
    paymentInFlight,
    showPaymentSection: canPayTotal
  });

  return (
    <div className="bg-white border-b border-gray-200 sticky top-0 z-10 shadow-sm">
      <div className="mx-auto px-6 py-4">
        <div className="flex items-center justify-between gap-4">
          {/* Logo/Advisor Info */}
          <div className="flex items-center gap-4">
            {sharedBy.logo_small_url ? (
              <img
                src={sharedBy.logo_small_url}
                alt={sharedBy.agency_name || `${sharedBy.first_name} ${sharedBy.last_name}`}
                className="h-8 sm:h-10 w-8 sm:w-10 object-contain"
              />
            ) : (
              <div className="h-8 sm:h-10 w-8 sm:w-10 rounded-full bg-primary/10 flex items-center justify-center">
                {sharedBy.agency_name ? (
                  <Building2 className="h-6 w-6 text-primary" />
                ) : (
                  <User className="h-6 w-6 text-primary" />
                )}
              </div>
            )}
            
            <div>
              <p className="text-xs sm:text-base text-gray-500">
                {t('sharedProposal.sharedBy')}
              </p>
              <p className="text-xs sm:text-base font-semibold text-gray-900">
                {`${sharedBy.first_name} ${sharedBy.last_name}`}
              </p>
            </div>
          </div>

          {/* Payment Section (right side, compact) */}
          {totalPrice && (
            <div className="flex items-center gap-4">
              {showError && (
                <Alert variant="destructive" className="py-1 px-3 flex items-center border-none">
                  <div className="flex items-center gap-2">
                    <AlertCircle className="h-3 w-3 flex-shrink-0" />
                    <AlertDescription className="text-xs">
                      {t(sharedPaymentErrorKey(errorMessage))}
                    </AlertDescription>
                  </div>
                </Alert>
              )}
              
              <div className="flex items-center gap-3">
                <div className="text-right">
                  <div className="text-xs text-gray-500">
                    {t('sharedProposal.totalPrice')}
                  </div>
                  <div className="flex items-center justify-end gap-1">
                    <div className="text-base sm:text-lg font-bold text-gray-900">
                      {formatPrice(totalPrice)}
                    </div>
                    {cancellationPolicyNotes && (
                      <button
                        onClick={() => setShowPolicyModal(true)}
                        className="flex-shrink-0"
                        aria-label={t('sharedProposal.viewCancellationPolicy')}
                      >
                        <Info className="h-4 w-4 text-gray-400 hover:text-gray-600 cursor-pointer" />
                      </button>
                    )}
                  </div>
                </div>

                {/* Il saldato viene prima: su un itinerario gia' incassato non c'e' piu'
                    niente da pagare, qualunque tentativo sia rimasto appeso. */}
                {paymentEnabled && settled ? (
                  <div className="flex items-center gap-1.5 text-green-700">
                    <CheckCircle2 className="h-4 w-4" />
                    <span className="text-sm font-medium">
                      {t('sharedProposal.payment.settledBadge')}
                    </span>
                  </div>
                ) : canPayTotal ? (
                  <div className="flex flex-col items-end gap-0.5">
                    {/* La spunta sta sopra il pulsante, non altrove nella pagina: il
                        consenso deve essere l'ultima cosa letta prima del click, non una
                        casella incontrata dieci schermate piu' su. */}
                    {hasLegalDocuments(legal) && onLegalAcceptedChange && (
                      <LegalAcceptance
                        legal={legal}
                        checked={legalAccepted}
                        onCheckedChange={onLegalAcceptedChange}
                        className="max-w-sm mb-1"
                      />
                    )}
                    <Button
                      onClick={onPayNow}
                      disabled={isCreatingLink || !canPay}
                      size="sm"
                      className="gap-2"
                    >
                      <CreditCard className="h-4 w-4" />
                      <span>
                        {isCreatingLink
                          ? t('sharedProposal.processingPayment')
                          : t('sharedProposal.payNow')}
                      </span>
                    </Button>
                    {paymentInFlight && (
                      <>
                        {/* Stesso badge delle rate: "in attesa" deve avere un aspetto solo. */}
                        <InstallmentStatusBadge status="pending" isProcessing />
                        <span className="hidden text-[11px] text-amber-700 sm:block max-w-[16rem] text-right leading-tight">
                          {t('sharedProposal.payment.retryHint')}
                        </span>
                      </>
                    )}
                    {/* Il bonifico non riporta il cliente qui: chi lo sceglie resta su
                        Stripe con l'IBAN. Meglio dirlo prima che dopo. */}
                    <span className="hidden text-[11px] text-gray-500 sm:block max-w-[16rem] text-right leading-tight">
                      {t('sharedProposal.payment.bankTransferNotice')}
                    </span>
                  </div>
                ) : null}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Cancellation Policy Modal */}
      <Dialog open={showPolicyModal} onOpenChange={setShowPolicyModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{t('sharedProposal.cancellationPolicy')}</DialogTitle>
          </DialogHeader>
          <div className="mt-4 text-sm text-gray-700 whitespace-pre-wrap">
            {cancellationPolicyNotes}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
